import { controlsRegion } from '../state.js';
import type { GameState, MapDefinition, PlayerId, TerritoryId } from '../types.js';
import { attackEv } from './odds.js';
import type { AiPersonality } from './personality.js';

/**
 * EV-adjustment helpers that wrap raw single-roll `attackEv` with
 * strategic awareness:
 *
 *  - **Threat multiplier** — multiply EV by (1 + threatWeight × overflow)
 *    where overflow is how much MORE territory the target's owner holds
 *    compared to the average living opponent. Floored at 1.0 so wounded
 *    players are never deprioritized — only leaders get boosted. This is
 *    the anti-kingmaker mechanic.
 *
 *  - **Elimination bonus** — flat +0.5 EV when the target tile is its
 *    owner's last territory. Personality-independent; no archetype passes
 *    on a logical kill. Pushes marginal kill-shot matchups through the
 *    threshold without forcing the AI to commit suicide on terrible
 *    matchups.
 *
 *  - **Region bonus** — adds territoryBonus × regionWeight when the
 *    capture would COMPLETE one of the AI's regions (next-turn payout)
 *    and territoryBonus × regionWeight × 0.5 when it BREAKS an enemy's
 *    currently-held region (denial — halved because the enemy can
 *    usually reclaim the broken tile). Both can stack.
 *
 *  - **Aggression bias** — flat additive from personality. Lets
 *    Aggressive AIs accept marginal fights Cautious ones reject.
 *
 * All four compose in `adjustedAttackEv`. Pure functions; no state
 * mutation, no RNG, no I/O.
 */

export interface ScoringContext {
  readonly state: GameState;
  readonly map: MapDefinition;
  readonly player: PlayerId;
  readonly personality: AiPersonality;
}

const ELIMINATION_BONUS_EV = 0.5;
// Denial is worth less than completion — capturing the broken tile
// gives me the bonus, but a savvy opponent often retakes it on their
// next turn before I see any payout. Half-credit is a rough discount.
const DENIAL_DISCOUNT = 0.5;

function countOwnedBy(state: GameState, owner: PlayerId): number {
  let n = 0;
  for (const ts of Object.values(state.territories)) {
    if (ts.owner === owner) n++;
  }
  return n;
}

/**
 * Fraction of total tiles owned by `who`. Used by the threat
 * multiplier and exposed for tests / future heuristics.
 */
export function playerTerritoryShare(state: GameState, who: PlayerId): number {
  const total = Object.keys(state.territories).length;
  if (total === 0) return 0;
  return countOwnedBy(state, who) / total;
}

/**
 * Mean territory share among living opponents (excluding `self` and
 * already-eliminated players). Used as the "neutral" baseline for the
 * threat multiplier — owners above this baseline are "leaders".
 */
function meanLivingOpponentShare(state: GameState, self: PlayerId): number {
  const opps = state.players.filter((p) => !p.eliminated && p.id !== self);
  if (opps.length === 0) return 0;
  const total = Object.keys(state.territories).length;
  if (total === 0) return 0;
  let owned = 0;
  for (const o of opps) owned += countOwnedBy(state, o.id);
  return owned / total / opps.length;
}

export function threatMultiplier(
  ctx: ScoringContext,
  targetTerritory: TerritoryId
): number {
  const ts = ctx.state.territories[targetTerritory];
  if (!ts) return 1;
  if (ts.owner === ctx.player) return 1;
  const targetShare = playerTerritoryShare(ctx.state, ts.owner);
  const meanShare = meanLivingOpponentShare(ctx.state, ctx.player);
  const overflow = Math.max(0, targetShare - meanShare);
  return 1 + ctx.personality.threatWeight * overflow;
}

export function eliminationBonus(
  ctx: ScoringContext,
  targetTerritory: TerritoryId
): number {
  const ts = ctx.state.territories[targetTerritory];
  if (!ts) return 0;
  if (ts.owner === ctx.player) return 0;
  return countOwnedBy(ctx.state, ts.owner) === 1 ? ELIMINATION_BONUS_EV : 0;
}

export function regionBonusEv(
  ctx: ScoringContext,
  targetTerritory: TerritoryId
): number {
  const ts = ctx.state.territories[targetTerritory];
  if (!ts) return 0;
  const tile = ctx.map.territories.find((t) => t.id === targetTerritory);
  if (!tile) return 0;
  const region = ctx.map.regions.find((r) => r.id === tile.region);
  if (!region) return 0;
  // Skip the math for bonus-zero regions — saves a couple of map
  // walks on ringMap-style maps where every region pays.
  if (region.territoryBonus <= 0) return 0;

  let bonus = 0;
  if (wouldCompleteRegion(ctx.state, ctx.map, targetTerritory, ctx.player)) {
    bonus += region.territoryBonus * ctx.personality.regionWeight;
  }
  if (
    ts.owner !== ctx.player &&
    controlsRegion(ctx.state, ctx.map, region.id, ts.owner)
  ) {
    bonus +=
      region.territoryBonus * ctx.personality.regionWeight * DENIAL_DISCOUNT;
  }
  return bonus;
}

function wouldCompleteRegion(
  state: GameState,
  map: MapDefinition,
  capturedTile: TerritoryId,
  asPlayer: PlayerId
): boolean {
  const tile = map.territories.find((t) => t.id === capturedTile);
  if (!tile) return false;
  for (const t of map.territories) {
    if (t.region !== tile.region) continue;
    if (t.id === capturedTile) continue; // assume captured
    const ts = state.territories[t.id];
    if (!ts || ts.owner !== asPlayer) return false;
  }
  return true;
}

/**
 * Composite EV used by AI scoring. Wraps single-roll `attackEv` with
 * threat multiplier, elimination bonus, region bonus, and personality
 * aggression bias.
 *
 * Returns -Infinity for invalid inputs (mirrors `attackEv`'s contract:
 * attacker needs ≥2 units, defender ≥1).
 */
export function adjustedAttackEv(
  ctx: ScoringContext,
  fromUnits: number,
  toUnits: number,
  targetTerritory: TerritoryId
): number {
  const base = attackEv(fromUnits, toUnits);
  if (!Number.isFinite(base)) return base;
  const mult = threatMultiplier(ctx, targetTerritory);
  const elim = eliminationBonus(ctx, targetTerritory);
  const region = regionBonusEv(ctx, targetTerritory);
  return base * mult + elim + region + ctx.personality.aggressionBias;
}
